import React from 'react';
import { reloadOnceForChunkError } from '../utils/chunkReload';

/**
 * ClientProfileErrorBoundary
 * ==========================
 * Error Boundary defensivo para la ficha de cliente (ClientNewProfile).
 *
 * PR-IS-CIERRE-PRG-1 · F3 (Ann 2026-05-24)
 * -----------------------------------------
 * Sin esta envoltura, cualquier throw en cualquier render del árbol de la
 * ficha (cierre IS, banner cierre programado, rejilla, modales, etc.) deja
 * la página COMPLETAMENTE en blanco, lo que confunde al admin sobre si la
 * operación se aplicó o no. Caso testigo Ann 2026-05-24 (cierre IS Luis
 * Toledano · síntoma "se queda en blanco y no hace el registro").
 *
 * Patrón clonado de SepaErrorBoundary (F3 PR1 · 2026-05-11). Mismo
 * comportamiento: panel rojo persistente con mensaje + stack + botón
 * recargar la sección sin perder el resto de la app.
 *
 * NO refactoriza ClientNewProfile · NO altera su lógica · únicamente
 * intercepta crashes de render para mostrar mensaje legible.
 */
class ClientProfileErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, ts: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Pestaña vieja tras un deploy → chunk inexistente: recarga 1 vez (anti-bucle).
    if (reloadOnceForChunkError(error)) return;
    this.setState({
      errorInfo,
      ts: new Date().toLocaleString('es-ES'),
    });
    console.error('[ClientProfileErrorBoundary] crash capturado:', error, errorInfo);
  }

  reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, ts: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const err = this.state.error;
    const msg = err?.message || String(err) || 'Error desconocido';
    const stack = (err?.stack || '').split('\n').slice(0, 6).join('\n');
    const componentStack = (this.state.errorInfo?.componentStack || '')
      .split('\n').slice(0, 6).join('\n');

    return (
      <div style={{
        padding: '20px',
        background: '#fef2f2',
        border: '1px solid #fca5a5',
        borderLeft: '4px solid #dc2626',
        borderRadius: '6px',
        color: '#7f1d1d',
        fontSize: '13px',
        lineHeight: 1.5,
        margin: '16px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '8px' }}>
              Error al cargar la ficha del cliente
            </div>
            <div style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '12px',
              background: '#fff',
              border: '1px solid #fecaca',
              borderRadius: '3px',
              padding: '8px 10px',
              marginBottom: '8px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}>
              <strong>Mensaje:</strong> {msg}
            </div>
            {stack && (
              <details style={{ marginTop: '8px' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  Detalles técnicos (stack)
                </summary>
                <pre style={{
                  fontFamily: 'ui-monospace, monospace',
                  fontSize: '11px',
                  background: '#fff',
                  border: '1px solid #fecaca',
                  borderRadius: '3px',
                  padding: '6px 8px',
                  marginTop: '6px',
                  overflowX: 'auto',
                  maxHeight: '200px',
                }}>{stack}{componentStack ? `\n--- component stack ---\n${componentStack}` : ''}</pre>
              </details>
            )}
            <div style={{ fontSize: '11px', color: '#9b2c2c', marginTop: '8px' }}>
              {this.state.ts ? `Ocurrido: ${this.state.ts}` : null}
              {' · '}
              La operación que estabas realizando puede o no haberse aplicado.
              Verifica el estado del cliente recargando la página antes de reintentar.
            </div>
          </div>
          <button
            onClick={this.reset}
            title="Reintentar"
            style={{
              background: '#fff',
              border: '1px solid #fca5a5',
              cursor: 'pointer',
              color: '#7f1d1d',
              fontSize: '13px',
              fontWeight: 600,
              padding: '6px 12px',
              borderRadius: '4px',
              whiteSpace: 'nowrap',
            }}
          >Recargar sección</button>
        </div>
      </div>
    );
  }
}

export default ClientProfileErrorBoundary;
